// ── El final de una sesión, en todas partes ──────────────────────────────────
//
// La mitad pura del cierre de sesión, sin Pinia ni Vue para que se pueda probar
// en Node (tests/sessionEvents.test.js).
//
// Dos cosas:
//
//   1. Pasos ANTES de revocar el token. Darse de baja del push es una llamada al
//      API, y con el token ya revocado contesta 401: en una tablet compartida
//      los avisos del anterior seguirían llegando con otra persona dentro. El
//      store no sabe de push (es de notifications); el armazón registra el paso.
//
//   2. Enterarse de que OTRA pestaña cerró la sesión. Los tokens viven en
//      localStorage, compartido por todas las pestañas del origen, y el evento
//      `storage` solo llega a las demás. Sin esto, la segunda pestaña seguía
//      enseñando lo que ya había cargado.

const steps = new Set()

/** Registra un paso que corre antes de revocar la sesión. Devuelve cómo quitarlo. */
export function onBeforeLogout(fn) {
  steps.add(fn)
  return () => steps.delete(fn)
}

/**
 * Corre todos los pasos a la vez. Nunca lanza y nunca espera más de `timeoutMs`:
 * un servicio de push que no contesta no puede dejar a nadie sin poder salir.
 */
export async function runBeforeLogout({ timeoutMs = 3000 } = {}) {
  const all = Promise.allSettled([...steps].map(fn => Promise.resolve().then(fn)))
  let timer
  const limit = new Promise(resolve => { timer = setTimeout(resolve, timeoutMs) })
  await Promise.race([all, limit])
  clearTimeout(timer)
}

/**
 * Llama a `onEnded` cuando otra pestaña borra el token de refresco (cerrar
 * sesión, sesión perdida) o vacía el almacenamiento entero. Un refresco
 * silencioso CAMBIA el valor y no cuenta.
 */
export function watchSessionEndedElsewhere(target, refreshKey, onEnded) {
  const handler = (event) => {
    if (event.key === null || (event.key === refreshKey && event.newValue === null)) onEnded()
  }
  target.addEventListener('storage', handler)
  return () => target.removeEventListener('storage', handler)
}
